import React, { useRef, useState, useEffect } from 'react';
import { ThemeProvider } from 'next-themes';
import { Card, CardContent, CardFooter, CardHeader } from '#AdminPanelFrontend/components/ui/card';
import { Input } from '#AdminPanelFrontend/components/ui/input';
import { Button } from '#AdminPanelFrontend/components/ui/button';
import { Label } from '#AdminPanelFrontend/components/ui/label';
import { ModeToggle } from '#AdminPanelFrontend/components/mode-toggle';
import logo from '#AdminPanelFrontend/styles/logo.png';
import { apiUrl } from '#AdminPanelFrontend/services/api';
import { socket } from '#AdminPanelFrontend/services/socket';
import { useSso } from '#AdminPanelFrontend/contexts/SsoContext';
import { Alert, AlertDescription, AlertTitle } from '#AdminPanelFrontend/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const Login = () => {
	const ssoConfig = useSso();
	const usernameRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);
	const rememberMeRef = useRef<HTMLInputElement>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const ssoError = params.get('sso_error');
		if (ssoError) {
			setError(decodeURIComponent(ssoError));
			window.history.replaceState({}, '', window.location.pathname);
		}

		const onLoginError = (data: { success?: boolean; message?: string }) => {
			if (!data.success) {
				setError(data.message ?? 'Login failed');
				setIsLoading(false);
			}
		};

		socket.on('User:Login', onLoginError);
		socket.on('User:SsoLogin', onLoginError);

		return () => {
			socket.off('User:Login', onLoginError);
			socket.off('User:SsoLogin', onLoginError);
		};
	}, []);

	const validate = () => {
		const errorList = [];
		setError(null);
		if (usernameRef.current?.value !== undefined && usernameRef.current?.value.length < 3) {
			errorList.push('Invalid username. It must be at least 3 characters long.');
		}

		if (passwordRef.current?.value !== undefined && passwordRef.current?.value.length < 8) {
			errorList.push('Invalid password. It must be at least 8 characters long.');
		}

		return errorList;
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const errorList = validate();
		if (errorList.length > 0) {
			setError(errorList.join('\n'));
			return;
		}
		setIsLoading(true);
		socket.timeout(5000).emit(
			'User:Login',
			{
				username: usernameRef.current?.value,
				password: passwordRef.current?.value,
				remember: rememberMeRef.current?.checked,
			},
			() => {
				setIsLoading(false);
			}
		);
	};

	const handleSsoLogin = () => {
		if (ssoConfig.loginUrl) {
			window.location.href = apiUrl(ssoConfig.loginUrl);
		}
	};

	if (ssoConfig.enabled) {
		return (
			<ThemeProvider attribute="class" defaultTheme="system" enableSystem>
				<div className="min-h-screen flex items-center justify-center bg-background">
					<Card className="w-[350px]">
						<CardHeader className="flex flex-col items-center">
							<ModeToggle />
							<div className="mb-4">
								<img src={logo} alt="the cavalcando logo" />
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							{error && (
								<Alert variant="destructive">
									<AlertCircle className="h-4 w-4" />
									<AlertTitle>Error</AlertTitle>
									<AlertDescription>{error}</AlertDescription>
								</Alert>
							)}
							<Button type="button" className="w-full" onClick={handleSsoLogin}>
								Sign in with Cavalcando SSO
							</Button>
						</CardContent>
					</Card>
				</div>
			</ThemeProvider>
		);
	}

	return (
		<ThemeProvider attribute="class" defaultTheme="system" enableSystem>
			<div className="min-h-screen flex items-center justify-center bg-background">
				<Card className="w-[350px]">
					<CardHeader className="flex flex-col items-center">
						<ModeToggle />
						<div className="mb-4">
							<img src={logo} alt="the cavalcando logo" />
						</div>
					</CardHeader>
					<form onSubmit={handleSubmit}>
						<CardContent className="space-y-4">
							{error && (
								<Alert variant="destructive">
									<AlertCircle className="h-4 w-4" />
									<AlertTitle>Error</AlertTitle>
									<AlertDescription>{error}</AlertDescription>
								</Alert>
							)}
							<div className="space-y-2">
								<Label htmlFor="username">Username</Label>
								<Input id="username" placeholder="Enter your username" ref={usernameRef} />
							</div>
							<div className="space-y-2">
								<Label htmlFor="password">Password</Label>
								<Input
									id="password"
									type="password"
									placeholder="Enter your password"
									ref={passwordRef}
								/>
							</div>
							<div className="items-top flex space-x-2">
								<input type="checkbox" id="lssk" ref={rememberMeRef} />
								<div className="grid gap-1.5 leading-none">
									<label
										htmlFor="lssk"
										className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
									>
										Remember me?
									</label>
								</div>
							</div>
						</CardContent>
						<CardFooter className="flex justify-center">
							<Button type="submit" disabled={isLoading}>
								Login
							</Button>
							&nbsp;
						</CardFooter>
					</form>
				</Card>
			</div>
		</ThemeProvider>
	);
};

export default Login;
